import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  List,
  ListItem,
  Flex,
  IconButton,
  SimpleGrid,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Badge,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Icon,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { hitBeApi } from "../../../api/api";
import { appStore } from "../../../store/Store";
import { CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiUsers } from "react-icons/fi";
import { DashboardActionShell } from "../utils/DashboardCategoryPanel";

function newMemberRowClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `nm_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Stable id per row so React keys don’t reuse the wrong inputs after delete/reorder. */
const emptyMemberRow = () => ({
  clientRowId: newMemberRowClientId(),
  name: "",
  email: "",
  phoneNumber: "",
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Digits only for validation (allow user to type spaces/dashes in UI). */
function digitsOnly(s) {
  return String(s || "").replace(/\D/g, "");
}

function isValidEmail(email) {
  const t = String(email || "").trim();
  if (!t) return false;
  return EMAIL_RE.test(t);
}

/** 10–15 digits — India mobile + international. */
function isValidPhoneDigits(phone) {
  const d = digitsOnly(phone);
  return d.length >= 10 && d.length <= 15;
}

function validateMemberRowForSubmit(row) {
  const name = String(row.name || "").trim();
  const email = String(row.email || "").trim();
  const phoneRaw = String(row.phoneNumber || "").trim();
  if (!name || name.length < 2) {
    return { ok: false, message: "Each member needs a name (at least 2 characters)." };
  }
  if (!email && !phoneRaw) {
    return {
      ok: false,
      message: "Each member needs an email and/or a valid phone number.",
    };
  }
  if (email && !isValidEmail(email)) {
    return { ok: false, message: "Enter a valid email address (e.g. name@example.com)." };
  }
  if (phoneRaw && !isValidPhoneDigits(phoneRaw)) {
    return {
      ok: false,
      message:
        "Phone must be 10–15 digits (digits only after stripping spaces).",
    };
  }
  return { ok: true };
}

const SplitwiseGroups = ({
  groups: groupsProp = [],
  setGroups,
  selectedGroup,
  setSelectedGroup,
  setShowModifyExpenses,
}) => {
  const groups = groupsProp;
  const [newGroupName, setNewGroupName] = useState("");
  const [openModifyMembers, setOpenModifyMembers] = useState(false);
  /** No default blank row — avoids a stuck “placeholder” the user can’t clear. */
  const [newGroupUsers, setNewGroupUsers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const toast = useToast();

  const borderColor = useColorModeValue("gray.300", "gray.600");
  const focusBorderColor = useColorModeValue("blue.500", "blue.300");
  const textColor = useColorModeValue("blue.700", "blue.200"); // Deep blue for a professional look
  const inputBorderColor = useColorModeValue("gray.300", "gray.600"); // Input border color
  const hoverBoxShadow = useColorModeValue("lg", "dark-lg");
  const cardBorder = useColorModeValue("gray.200", "gray.600");
  const accentBg = useColorModeValue("teal.50", "rgba(20, 184, 166, 0.12)");
  const pageMuted = useColorModeValue("gray.600", "gray.400");
  const selectedTitleColor = useColorModeValue("gray.800", "gray.100");
  const cardSurface = useColorModeValue("white", "gray.800");
  const selectedPanelHeaderBg = useColorModeValue("gray.50", "gray.900");
  const emptyStateBg = useColorModeValue("gray.50", "gray.900");

  /** Forces member list to remount when membership changes (avoids stale UI). */
  const membersRenderKey = useMemo(
    () =>
      (selectedGroup?.members || [])
        .map((m) => String(m._id))
        .sort()
        .join(","),
    [selectedGroup?.members]
  );

  /** Keep group cards + parent `currentGroup` in sync when a group object is updated. */
  const replaceGroupInList = (group) => {
    if (!group?._id) return;
    setGroups((prev) => {
      const id = String(group._id);
      const i = prev.findIndex((g) => String(g._id) === id);
      const copy = [...prev];
      if (i === -1) return [...prev, group];
      copy[i] = group;
      return copy;
    });
  };

  // Fetch all groups on component mount
  const fetchGroups = async () => {
    try {
      const response = await hitBeApi("splitwise/groups", "GET", null);
      if (response.success) {
        const data = response.data;
        setGroups(data);
      }
    } catch (error) {
      toast({
        title: "Error fetching groups.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle group creation
  const createGroup = async () => {
    const trimmed = newGroupName.trim();
    if (!trimmed || trimmed.length < 2) {
      toast({
        title: "Validation Error.",
        description: "Enter a group name (at least 2 characters).",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newGroup = { name: trimmed };
    try {
      const createdGroup = await hitBeApi(
        "splitwise/groups/create",
        "POST",
        newGroup
      );
      if (createdGroup.success) {
        await fetchGroups();
        toast({
          title: "Group created.",
          description: `Group "${trimmed}" created successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setNewGroupName("");
      }
    } catch (error) {
      toast({
        title: "Error creating group.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle adding members to a group
  const addMembersToGroup = async () => {
    if (!selectedGroup || selectedGroup === undefined) {
      toast({
        title: "Validation Error.",
        description: "Please select a group to add members.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const rowsToAdd = newGroupUsers.filter((u) => {
      const n = String(u.name || "").trim();
      const e = String(u.email || "").trim();
      const p = digitsOnly(u.phoneNumber);
      return n.length > 0 || e.length > 0 || p.length > 0;
    });
    if (rowsToAdd.length === 0) {
      toast({
        title: "Nothing to add",
        description:
          'Use "Add a member row", then enter name and email or phone. Empty rows are ignored.',
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    for (let i = 0; i < rowsToAdd.length; i++) {
      const check = validateMemberRowForSubmit(rowsToAdd[i]);
      if (!check.ok) {
        toast({
          title: "Check member details",
          description: `Row ${i + 1}: ${check.message}`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    const payloadUsers = rowsToAdd.map((u) => ({
      name: String(u.name || "").trim(),
      email: String(u.email || "").trim() || undefined,
      phoneNumber: digitsOnly(u.phoneNumber) || undefined,
    }));

    try {
      const updatedGroup = { users: payloadUsers };
      const updatedGroupRes = await hitBeApi(
        `splitwise/groups/${selectedGroup?._id}/add-members`,
        "POST",
        updatedGroup
      );
      if (updatedGroupRes.success) {
        const next = updatedGroupRes.data;
        const serverMsg =
          typeof updatedGroupRes.message === "string"
            ? updatedGroupRes.message
            : "";
        const looksLikeNoNew = /no new members/i.test(serverMsg);
        appStore.dispatch({ type: "CLEAR_API_CACHE" });
        // Update selected group immediately so the member list reflects new rows without waiting.
        setSelectedGroup(next);
        replaceGroupInList(next);
        toast({
          title: looksLikeNoNew ? "No new members" : "Members updated",
          description:
            serverMsg ||
            `Updated group "${selectedGroup.name}".`,
          status: looksLikeNoNew ? "info" : "success",
          duration: 5000,
          isClosable: true,
        });
        setNewGroupUsers([]);
        setEditingMember(null);
        // Refetch so group cards stay in sync; GET bypasses cache for splitwise (see api.js).
        try {
          const refreshed = await hitBeApi("splitwise/groups", "GET", null);
          if (refreshed.success && Array.isArray(refreshed.data)) {
            setGroups(refreshed.data);
            const gid = String(next._id);
            const merged = refreshed.data.find((g) => String(g._id) === gid);
            if (merged?.members?.length >= (next.members?.length ?? 0)) {
              setSelectedGroup(merged);
            }
          }
        } catch {
          /* list already updated from POST response */
        }
      }
    } catch (error) {
      toast({
        title: "Error adding users.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle deleting a member
  const deleteMember = async (groupId, memberId) => {
    try {
      await hitBeApi(
        `splitwise/groups/${groupId}/delete-member/${memberId}`,
        "DELETE"
      );
      // Remove the member from the matched group's members array
      const updatedMembers = (selectedGroup.members || []).filter(
        (member) => String(member._id) !== String(memberId)
      );

      // Update the matched group with the new members list
      const updatedGroup = { ...selectedGroup, members: updatedMembers };

      replaceGroupInList(updatedGroup);
      setSelectedGroup(updatedGroup);
      toast({
        title: "Member deleted.",
        description: "User removed from the group successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error deleting member.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle deleting a group
  const deleteGroup = async (groupId) => {
    try {
      const response = await hitBeApi(`splitwise/groups/${groupId}`, "DELETE");
      if (response.success) {
        toast({
          title: "Group deleted.",
          description: "Group removed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchGroups(); // Refresh the groups list
        setSelectedGroup(null);
      } else {
        toast({
          title: "Error deleting group.",
          description: response.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error deleting group.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle editing a member's details
  const updateMember = async (groupId, memberId, updatedDetails) => {
    try {
      const updatedMemberDetails = await hitBeApi(
        `splitwise/groups/${groupId}/update-member/${memberId}`,
        "PUT",
        updatedDetails
      );
      if (updatedMemberDetails.success) {
        toast({
          title: "Member updated.",
          description: "User details updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchGroups(); // Fetch updated groups
        setEditingMember(null); // Clear editing state
        // Update the members array with the updated member details
        const updatedMembers = (selectedGroup.members || []).map((member) =>
          member._id.toString() === memberId.toString()
            ? updatedMemberDetails?.data
            : member
        );

        // Create a new group object with the updated members
        const updatedGroup = { ...selectedGroup, members: updatedMembers };

        replaceGroupInList(updatedGroup);
        setSelectedGroup(updatedGroup);
      }
    } catch (error) {
      toast({
        title: "Error updating member.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleNewMemberRowChange = (clientRowId, key, value) => {
    setNewGroupUsers((prev) =>
      prev.map((u) =>
        u.clientRowId === clientRowId ? { ...u, [key]: value } : u
      )
    );
  };

  const addUser = () => {
    setNewGroupUsers((prev) => [...prev, emptyMemberRow()]);
  };

  const removeNewMemberRow = (clientRowId) => {
    if (clientRowId == null) return;
    setNewGroupUsers((prev) => prev.filter((u) => u.clientRowId !== clientRowId));
  };

  /** Opening the members modal: fresh add-member form (no phantom blank row). */
  const openMembersModal = () => {
    setNewGroupUsers([]);
    setEditingMember(null);
    setOpenModifyMembers(true);
  };

  // Toggle edit mode for a member
  const startEditingMember = (groupId, member) => {
    const g = groups.find((group) => group._id === groupId) || selectedGroup;
    if (g) setSelectedGroup(g);
    setEditingMember(member);
  };

  const selectedId = selectedGroup?._id ? String(selectedGroup._id) : null;

  const handleMemberInputChange = (key, value) => {
    setEditingMember({ ...editingMember, [key]: value });
  };

  // Render group list
  const renderGroups = () => {
    if (!groups?.length) {
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={12}
          px={4}
          borderRadius="xl"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={cardBorder}
          bg={emptyStateBg}
        >
          <Icon as={FiUsers} boxSize={12} color="teal.400" mb={4} />
          <Text fontSize="sm" color={pageMuted} textAlign="center" maxW="md">
            No groups yet. Create one above to add members and expenses.
          </Text>
        </Flex>
      );
    }
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} width="100%">
        {groups.map((group) => {
          const isActive = selectedId && String(group._id) === selectedId;
          return (
            <Card
              key={group._id}
              variant="outline"
              w="100%"
              borderRadius="xl"
              borderWidth={isActive ? "2px" : "1px"}
              borderColor={isActive ? "teal.500" : cardBorder}
              borderLeftWidth="4px"
              borderLeftColor={isActive ? "teal.500" : "teal.400"}
              shadow={isActive ? "lg" : "sm"}
              bg={isActive ? cardSurface : accentBg}
              onClick={() => setSelectedGroup(group)}
              cursor="pointer"
              transition="all 0.2s ease"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: hoverBoxShadow,
                borderColor: "teal.300",
              }}
            >
              <CardBody p={5}>
                <Text fontWeight="bold" fontSize="md" color={textColor} mb={2} noOfLines={2}>
                  {group.name}
                </Text>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge colorScheme="teal" variant={isActive ? "solid" : "subtle"} borderRadius="md">
                    {group.members?.length ?? 0} member
                    {(group.members?.length || 0) === 1 ? "" : "s"}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color={pageMuted}>
                  Created{" "}
                  {group.createdAt ? new Date(group.createdAt).toLocaleDateString() : "—"}
                </Text>
              </CardBody>
            </Card>
          );
        })}
      </SimpleGrid>
    );
  };

  return (
    <VStack spacing={{ base: 6, md: 7 }} align="stretch" w="full">
        <Box>
          <Text
            fontSize="xs"
            fontWeight="bold"
            color={pageMuted}
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={3}
          >
            Groups
          </Text>
          <Text fontSize="sm" color={pageMuted} mb={4} maxW="3xl" lineHeight="tall">
            Tap a card to select a group. Members and expenses use the buttons in the
            selected group panel below.
          </Text>
        </Box>

        <DashboardActionShell
          kind="splitwise"
          badgeLabel="New group"
          title="Create a group"
        >
          <Input
            placeholder="Group name (e.g. Weekend trip)"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            mb={3}
            borderColor={borderColor}
            _focus={{
              borderColor: focusBorderColor,
              boxShadow: `0 0 0 1px ${focusBorderColor}`,
            }}
          />
          <Button colorScheme="teal" onClick={createGroup} width="100%" mt={1}>
            Create group
          </Button>
        </DashboardActionShell>

        <Box w="100%">{renderGroups()}</Box>

        {selectedGroup && (
          <Card
            key={selectedGroup?._id}
            w="100%"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            overflow="hidden"
            boxShadow="md"
            bg={cardSurface}
            borderTopWidth="4px"
            borderTopColor="teal.400"
          >
            <CardHeader
              bg={selectedPanelHeaderBg}
              borderBottomWidth="1px"
              borderColor={borderColor}
              py={{ base: 4, md: 5 }}
            >
              <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap">
                <HStack align="flex-start" spacing={3}>
                  <Flex
                    align="center"
                    justify="center"
                    w={10}
                    h={10}
                    borderRadius="lg"
                    bg={accentBg}
                    color="teal.600"
                    flexShrink={0}
                  >
                    <Icon as={FiUsers} boxSize={5} />
                  </Flex>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={pageMuted}
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                    >
                      Selected group
                    </Text>
                    <Heading as="h2" size="md" fontWeight="bold" color={selectedTitleColor} mt={1}>
                      {selectedGroup.name}
                    </Heading>
                  </Box>
                </HStack>
                <IconButton
                  aria-label="Clear selection"
                  icon={<CloseIcon />}
                  size="sm"
                  colorScheme="gray"
                  variant="ghost"
                  onClick={() => setSelectedGroup(null)}
                />
              </Flex>
            </CardHeader>
            <CardBody px={{ base: 4, md: 6 }} py={5}>
              <Flex gap={3} flexDirection={{ base: "column", sm: "row" }} flexWrap="wrap">
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={openMembersModal}
                  variant="solid"
                  borderRadius="lg"
                >
                  Members
                </Button>
                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={() => {
                    setSelectedGroup(selectedGroup);
                    setShowModifyExpenses(true);
                  }}
                  variant="solid"
                  borderRadius="lg"
                >
                  Expenses
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  onClick={() => deleteGroup(selectedGroup._id)}
                  variant="outline"
                  borderRadius="lg"
                >
                  Delete group
                </Button>
              </Flex>
            </CardBody>
          </Card>
        )}

        <Modal
          isOpen={Boolean(selectedGroup && openModifyMembers)}
          onClose={() => {
            setOpenModifyMembers(false);
            setEditingMember(null);
            setNewGroupUsers([]);
          }}
          size="xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader pr={10}>
              Members — {selectedGroup?.name}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Current members can edit or remove. Add rows below — each new member
                needs a name and at least one of email or phone (10–15 digits).
              </Text>
              <Divider mb={4} />
              {selectedGroup?.members && selectedGroup.members.length > 0 ? (
                <List key={membersRenderKey} spacing={4} mb={6}>
                  {selectedGroup.members.map((member) => (
                    <ListItem key={String(member._id)}>
                      <Box
                        p={3}
                        rounded="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                      >
                        <Flex
                          flexDirection={{ base: "column", md: "row" }}
                          justify="space-between"
                          gap={3}
                          align={{ md: "center" }}
                        >
                          <Box>
                            <Text fontWeight="semibold" color={textColor}>
                              {member.name}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {member.email} · {member.phoneNumber || "—"}
                            </Text>
                          </Box>
                          <HStack flexShrink={0}>
                            <Button
                              size="sm"
                              colorScheme="teal"
                              variant="outline"
                              onClick={() =>
                                startEditingMember(selectedGroup._id, member)
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() =>
                                deleteMember(selectedGroup._id, member._id)
                              }
                            >
                              Remove
                            </Button>
                          </HStack>
                        </Flex>

                        {editingMember && editingMember._id === member._id && (
                          <Box
                            mt={3}
                            p={4}
                            borderWidth="1px"
                            borderColor={borderColor}
                            rounded="md"
                          >
                            <Text fontSize="sm" fontWeight="semibold" mb={3}>
                              Edit member
                            </Text>
                            <VStack spacing={3}>
                              <Input
                                placeholder="User Name"
                                value={editingMember.name}
                                onChange={(e) =>
                                  handleMemberInputChange("name", e.target.value)
                                }
                              />
                              <Input
                                placeholder="User Email"
                                value={editingMember.email}
                                onChange={(e) =>
                                  handleMemberInputChange("email", e.target.value)
                                }
                              />
                              <Input
                                placeholder="Phone"
                                value={editingMember.phoneNumber}
                                type="tel"
                                onChange={(e) =>
                                  handleMemberInputChange(
                                    "phoneNumber",
                                    e.target.value
                                  )
                                }
                              />
                              <HStack spacing={3} w="full" justify="flex-end">
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() =>
                                    updateMember(
                                      selectedGroup._id,
                                      editingMember._id,
                                      editingMember
                                    )
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingMember(null)}
                                >
                                  Cancel
                                </Button>
                              </HStack>
                            </VStack>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Text color={textColor} fontStyle="italic" mb={4}>
                  No members yet — add people below.
                </Text>
              )}

              <Text fontWeight="bold" mb={1}>
                Add new members
              </Text>
              <Text fontSize="sm" color="gray.500" mb={3}>
                Add one or more rows. Each row needs a <strong>name</strong> and at
                least one of <strong>email</strong> or <strong>phone</strong>{" "}
                (10–15 digits). There is no blank row until you choose{" "}
                <strong>Add a member row</strong>.
              </Text>
              <Button
                mb={newGroupUsers.length ? 4 : 2}
                size="sm"
                colorScheme="teal"
                variant="solid"
                onClick={addUser}
              >
                Add a member row
              </Button>
              {newGroupUsers.length === 0 ? (
                <Text fontSize="sm" color="gray.500" fontStyle="italic" mb={2}>
                  No draft rows — use the button above to start.
                </Text>
              ) : null}
              {newGroupUsers.map((user) => (
                <SimpleGrid
                  key={user.clientRowId}
                  columns={{ base: 1, md: 12 }}
                  spacing={3}
                  mt={3}
                  alignItems="flex-start"
                >
                  <Box gridColumn={{ md: "span 3" }}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Name (required)
                    </Text>
                    <Input
                      placeholder="e.g. Priya Sharma"
                      value={user.name}
                      onChange={(e) =>
                        handleNewMemberRowChange(
                          user.clientRowId,
                          "name",
                          e.target.value
                        )
                      }
                      borderColor={inputBorderColor}
                    />
                  </Box>
                  <Box gridColumn={{ md: "span 4" }}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Email
                    </Text>
                    <Input
                      placeholder="Leave blank if you use phone only"
                      type="email"
                      autoComplete="email"
                      value={user.email}
                      onChange={(e) =>
                        handleNewMemberRowChange(
                          user.clientRowId,
                          "email",
                          e.target.value
                        )
                      }
                      borderColor={inputBorderColor}
                    />
                  </Box>
                  <Box gridColumn={{ md: "span 4" }}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Phone
                    </Text>
                    <Input
                      placeholder="Leave blank if you use email only"
                      value={user.phoneNumber}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      onChange={(e) =>
                        handleNewMemberRowChange(
                          user.clientRowId,
                          "phoneNumber",
                          e.target.value
                        )
                      }
                      borderColor={inputBorderColor}
                    />
                  </Box>
                  <Box gridColumn={{ md: "span 1" }} pt={{ base: 0, md: 6 }}>
                    <IconButton
                      icon={<DeleteIcon />}
                      aria-label="Remove this draft row"
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (
                          window.confirm(
                            "Remove this row from the form? (You can add a new row anytime.)"
                          )
                        ) {
                          removeNewMemberRow(user.clientRowId);
                        }
                      }}
                    />
                  </Box>
                </SimpleGrid>
              ))}
            </ModalBody>
            <ModalFooter gap={3} flexWrap="wrap">
              <Button
                variant="ghost"
                onClick={() => {
                  setOpenModifyMembers(false);
                  setEditingMember(null);
                  setNewGroupUsers([]);
                }}
              >
                Close
              </Button>
              <Button
                colorScheme="green"
                onClick={() => addMembersToGroup()}
              >
                Save & add members
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
    </VStack>
  );
};

export default SplitwiseGroups;
